import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Tg24VideoView, ITg24VideoViewModel } from '@sky-it-video/video-hlroom-design-system';
import VideoHlroomAnalytics from '@sky-it-video/hlroom-analytics';

import { useHash } from '../hooks/react-use/useHash';
import { doGetVideoDataSearchFetch } from '../hooks/doGetVideoDataSearchFetch';

import {
  getIndexesFromHash,
  createOnSubSectionChange,
  createOnSubSubSectionChange,
  getSubSectionFromHash,
  getTagsFromHash,
  createCreateLoadMoreFn,
} from './helpers';

const Tg24roomMainWrapper: React.FC<ITg24VideoViewModel> = ({
  subSections: ssrSubSections,
  videos: ssrVideos,
  videosTotal: ssrVideosTotal,
  parentStickySelector,
}) => {
  const [page, setPage] = useState(0);
  const [hash, setHash] = useHash();
  const [hashTags, setHashTags] = useState<string[]>([]);
  const [videos, setVideos] = useState<any>([]);
  const [videosTotal, setVideosTotal] = useState<number>(0);
  const [noVideosByFilter, setNoVideosByFilter] = useState<boolean>(false);

  const onSubSectionChange = createOnSubSectionChange(setHash);
  const onSubSubSectionChange = createOnSubSubSectionChange(setHash, hash);
  const createLoadMoreFn = createCreateLoadMoreFn(setPage);

  const handleVideosReset = async (hash: string, page: number) => {
    const newHashTags = getTagsFromHash(hash);
    const data = await doGetVideoDataSearchFetch(newHashTags, page);

    if (data.videosTotal > 0) {
      setVideos(data.videos);
      setVideosTotal(data.videosTotal);
      setHashTags(newHashTags);
    } else {
      const subSectionTag = getSubSectionFromHash(hash);
      let data;
      if (subSectionTag) {
        data = await doGetVideoDataSearchFetch([subSectionTag], 0);
        if (data.videosTotal > 0) {
          setVideos(data.videos);
          setVideosTotal(data.videosTotal);
          setHashTags([subSectionTag]);
          setNoVideosByFilter(true);
        } else {
          data = await doGetVideoDataSearchFetch([], 0);
          setVideos(data.videos);
          setVideosTotal(data.videosTotal);
          setHashTags([]);
          setNoVideosByFilter(true);
        }
      } else {
        data = await doGetVideoDataSearchFetch([], 0);
        setVideos(data.videos);
        setVideosTotal(data.videosTotal);
        setHashTags([]);
        setNoVideosByFilter(true);
      }
    }
  };

  useEffect(() => {
    (async () => {
      // console.log('OnMount hash = %s, page = %s, hashTags = %o', hash, page);
      await handleVideosReset(hash, page);
      VideoHlroomAnalytics.analyticsTrack(VideoHlroomAnalytics.CONFIGS.tg24.PAGE);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (videos.length > 0) {
      (async () => {
        // console.log('OnHashChange hash = %s, page = %s, videos = %s => ', hash, page, videos.length);
        setVideos([]);
        setVideosTotal(0);
        setNoVideosByFilter(false);
        await handleVideosReset(hash, 0);
        if (page > 0) {
          setPage(0);
        }
      })();
    }
  }, [hash]);

  useEffect(() => {
    if (videos.length > 0 && page > 0) {
      (async () => {
        // console.log('OnPageChange hashTags = %o, page = %s', hashTags, page);
        const { videos: moreVideos } = await doGetVideoDataSearchFetch(hashTags, page);
        setVideos(videos.concat(moreVideos));
      })();
    }
  }, [page]);

  const [subSectionIndex, subSubSectionIndex] = getIndexesFromHash(hash, ssrSubSections);

  return (
    <Tg24VideoView
      subSections={ssrSubSections}
      videos={videos}
      videosTotal={videosTotal}
      loadMoreFn={createLoadMoreFn(videosTotal, videos.length)}
      onSubSubSectionChange={onSubSubSectionChange}
      onSubSectionChange={onSubSectionChange}
      subSectionIndex={subSectionIndex}
      subSubSectionIndex={subSubSectionIndex}
      noVideosByFilter={noVideosByFilter}
      parentStickySelector={parentStickySelector}
    />
  );
};

Tg24roomMainWrapper.propTypes = {
  // eslint-disable-next-line react/require-default-props
  // parentStickySelector: PropTypes.string,
  // TODO improve this stuff!
  subSections: PropTypes.any,
  videos: PropTypes.any,
  videosTotal: PropTypes.any,
};

// TODO write defaultProps?
// HlroomMainWrapper.defaultProps = {
// };

export default Tg24roomMainWrapper;
